import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router';
import { useUser } from "@clerk/clerk-react";
import { Navigate } from "@tanstack/react-router";
import { getOnboardingStep, getUserFromClerkId } from '../hooks/useUser';
import { userHasPaidAccess } from '@/lib/userSubscriptionHelpers';
import { BrandedSpinner } from '@/components/BrandedSpinner';

export const Route = createFileRoute('/_auth')({
  validateSearch: (search: Record<string, unknown>) => {
    const result: {
      invitationcode?: string;
      tour?: string;
      __clerk_ticket?: string;
    } = {};
    if (typeof search.invitationcode === 'string') result.invitationcode = search.invitationcode;
    if (typeof search.tour === 'string') result.tour = search.tour;
    if (typeof search.__clerk_ticket === 'string') result.__clerk_ticket = search.__clerk_ticket;
    return result;
  },
  component: AuthGate,
});

function AuthGate() {
  const { user, isLoaded } = useUser();
  const search = Route.useSearch();
  const location = useLocation();
  const onboardingStepQuery = getOnboardingStep();
  const { data: userData, isLoading: userLoading } = getUserFromClerkId(user?.id ?? '', { enabled: !!user });
  const hasPaidAccess = userHasPaidAccess(userData);

  if (!isLoaded) return (
    <div className="flex min-h-[calc(100dvh-56px)] md:min-h-[calc(100dvh-64px)] items-center justify-center">
      <BrandedSpinner size="xl" />
    </div>
  );

  if (!user) {
    return <Navigate to="/signin" search={{ invitationcode: search.invitationcode, __clerk_ticket: search.__clerk_ticket }} />;
  }

  // Only redirect to /invite if not already there
  if (
    search.invitationcode &&
    !location.pathname.endsWith('/invite')
  ) {
    return <Navigate to="/invite" search={{ invitationcode: search.invitationcode, __clerk_ticket: undefined }} />;
  }

  // Pages that should never be redirected away from for onboarding
  const onboardingExemptPages = ['/onboarding', '/invite', '/checkout-success', '/checkout-cancel'];
  const isOnboardingExempt = onboardingExemptPages.some(page => location.pathname.endsWith(page));

  // Wait for queries to settle before rendering protected content
  if (!isOnboardingExempt && (onboardingStepQuery.isLoading || onboardingStepQuery.isPending || userLoading)) {
    return (
      <div className="flex min-h-[calc(100dvh-56px)] md:min-h-[calc(100dvh-64px)] items-center justify-center">
        <BrandedSpinner size="xl" />
      </div>
    );
  }

  // Only check onboarding for paid users. Unpaid users will see the
  // subscription modal (from __root.tsx) instead of being sent to onboarding.
  if (
    hasPaidAccess &&
    onboardingStepQuery.data !== 'COMPLETE' &&
    !isOnboardingExempt
  ) {
    return <Navigate to="/onboarding" search={{ invitationcode: search.invitationcode, __clerk_ticket: search.__clerk_ticket }} />;
  }

  return <Outlet />;
}
